import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

interface ArticleLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbLabel: string;
  featuredImage: string;
  featuredImageAlt: string;
  tag?: string;
  readTime?: string;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  subtitle,
  breadcrumbLabel,
  featuredImage,
  featuredImageAlt,
  tag,
  readTime,
  children,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: subtitle || title,
    image: featuredImage,
    datePublished: '2026-08-01',
    dateModified: '2026-08-09',
    author: {
      '@type': 'Organization',
      name: 'SugargooSheet',
      url: 'https://sugargoosheet.bond',
    },
    publisher: {
      '@type': 'Organization',
      name: 'SugargooSheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
  };

  return (
    <>
      <Navbar />
      <JsonLd data={jsonLd} />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="mb-8 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-150">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">{breadcrumbLabel}</span>
          </nav>

          <header>
            {tag && (
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 whitespace-nowrap">
                {tag}
              </span>
            )}
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{subtitle}</p>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </span>
                Updated August 2026
              </span>
              {readTime && (
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </span>
                  {readTime} min read
                </span>
              )}
            </div>
          </header>

          <div className="mt-8 w-full aspect-[21/9] rounded-lg overflow-hidden">
            <img
              src={featuredImage}
              alt={featuredImageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {children}

          <div className="mt-14 pt-8 border-t border-background-200">
            <div className="p-6 rounded-lg bg-primary-50 border border-primary-200 text-center">
              <p className="text-sm text-foreground-700">
                Ready to explore more?{' '}
                <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-150">
                  Return to our homepage
                </Link>
                {' '}to browse all Sugargoo spreadsheet categories and discover trending products across shoes, clothing, accessories, and more.
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              Explore Sugargoo Spreadsheet Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}